// Import DSP functions directly from platform source (bypassing the index to avoid
// the `idb` dependency pulled in by cache.ts; analysis runs inline, no Worker needed).
import { analyzeLoudness } from '../../../../platform/packages/client-analysis/src/loudness.ts';
import {
  analyzeSpectral,
  analyzeTransients,
} from '../../../../platform/packages/client-analysis/src/spectral.ts';
import { analyzeStereo } from '../../../../platform/packages/client-analysis/src/stereo.ts';
import { detectResonancePeaks } from '../../../../platform/packages/client-analysis/src/resonance.ts';
import { sibilanceBandEnergyDb } from '../../../../platform/packages/client-analysis/src/sibilance.ts';
import { parseWav32f } from '../../../../platform/packages/client-analysis/src/wav.ts';
import type { TrackAnalysis } from '../../../../platform/packages/client-analysis/src/types.ts';

import type { Dispatcher } from '../dispatcher.js';
import { ToolError } from '../dispatcher.js';
import { ERROR_CODES } from '../wsClient.js';
import { serializeSession } from '../serializer.js';
import type { Track } from '../serializer.types.js';
import { getStudioContext, nowIso, store } from './store.js';

// In-memory cache keyed by audio_blob_hash so repeated agent calls in the same
// browser session don't re-run expensive DSP.
const analysisCache = new Map<string, TrackAnalysis>();

// Read an audio blob from OPFS by AudioFileBox UUID.
// openDAW stores audio at samples/v2/{UUID}/audio.wav.
async function readAudioBlob(uuid: string): Promise<Blob | null> {
  if (!uuid) return null;
  const storage = (globalThis as { navigator?: Navigator }).navigator?.storage;
  if (!storage || typeof storage.getDirectory !== 'function') return null;
  try {
    const root = await storage.getDirectory();
    const samplesDir = await root.getDirectoryHandle('samples');
    const v2Dir = await samplesDir.getDirectoryHandle('v2');
    const uuidDir = await v2Dir.getDirectoryHandle(uuid);
    const handle = await uuidDir.getFileHandle('audio.wav');
    return handle.getFile();
  } catch (err) {
    if ((err as { name?: string }).name === 'NotFoundError') return null;
    throw err;
  }
}

// Run all DSP analysis on a decoded WAV blob. Results are cached by hash.
async function analyzeBlob(blob: Blob, hash: string): Promise<TrackAnalysis> {
  const cached = analysisCache.get(hash);
  if (cached) return cached;

  const buf = await blob.arrayBuffer();
  const decoded = parseWav32f(buf);
  const { channels, sample_rate } = decoded;

  const mono = sumToMono(channels);
  const loudness = analyzeLoudness({ samples: channels, sample_rate });
  const spectral = analyzeSpectral({ samples: mono, sample_rate });
  const transients = analyzeTransients({ samples: mono, sample_rate });
  const stereo =
    channels.length >= 2
      ? analyzeStereo({ left: channels[0], right: channels[1], sample_rate })
      : { correlation: 1, ms_ratio: 1e6, width_by_band: new Array(10).fill(1) as number[] };
  const resonance_peaks_hz = detectResonancePeaks({ samples: mono, sample_rate });
  const sibilance_band_energy_db = sibilanceBandEnergyDb({ samples: mono, sample_rate });

  const result: TrackAnalysis = {
    computed_at: nowIso(),
    audio_blob_hash: hash,
    loudness,
    spectral,
    stereo,
    transients,
    resonance_peaks_hz,
    sibilance_band_energy_db,
    computed_by: {
      loudness: 'client',
      spectral: 'client',
      stereo: 'client',
      transients: 'client',
      resonance_peaks_hz: 'client',
      sibilance_band_energy_db: 'client',
    },
  };

  if (hash) analysisCache.set(hash, result);
  return result;
}

function sumToMono(channels: Float32Array[]): Float32Array {
  if (channels.length === 0) return new Float32Array(0);
  if (channels.length === 1) return channels[0];
  const len = channels[0].length;
  const out = new Float32Array(len);
  const inv = 1 / channels.length;
  for (const ch of channels) {
    for (let i = 0; i < len; i++) out[i] += ch[i] * inv;
  }
  return out;
}

// Analyze a single track, returning null if no audio is loaded.
async function analyzeTrackById(track: Track): Promise<TrackAnalysis | null> {
  const hash = track.regions?.[0]?.audio_blob_hash ?? '';
  const blob = await readAudioBlob(hash);
  if (!blob) return null;
  return analyzeBlob(blob, hash);
}

function getSessionTracks(): Track[] {
  const ctx = getStudioContext();
  if (ctx !== null) return serializeSession(ctx.getProject()).tracks;
  return store.getProject().tracks.map((t) => ({
    ...t,
    arm: false,
    inserts: [],
    sends: [],
    output_routing: 'master',
    input_routing: null,
    regions: [],
    automation: [],
    frozen: false,
  }));
}

// Combine multiple LUFS values into a single equivalent power using BS.1770
// power-sum: L_combined = 10 * log10(sum(10^(L_i/10))).
function combineLufs(values: number[]): number {
  const finite = values.filter(isFinite);
  if (finite.length === 0) return -Infinity;
  const powerSum = finite.reduce((acc, l) => acc + Math.pow(10, l / 10), 0);
  return 10 * Math.log10(powerSum);
}

function avgNumber(values: number[]): number {
  const finite = values.filter(isFinite);
  if (finite.length === 0) return 0;
  return finite.reduce((a, b) => a + b, 0) / finite.length;
}

function avgArrays(arrays: number[][]): number[] {
  if (arrays.length === 0) return [];
  const len = arrays[0].length;
  const out = new Array<number>(len).fill(0);
  for (const arr of arrays) {
    for (let i = 0; i < len; i++) out[i] += arr[i];
  }
  return out.map((v) => v / arrays.length);
}

// Build a synthetic master TrackAnalysis by aggregating per-track analyses.
function combineMaster(analyses: TrackAnalysis[], hash: string): TrackAnalysis {
  if (analyses.length === 0) {
    return {
      computed_at: nowIso(),
      audio_blob_hash: hash,
      loudness: {
        lufs_integrated: -Infinity,
        lufs_short_term: -Infinity,
        lufs_momentary_max: -Infinity,
        true_peak_dbtp: -Infinity,
        crest_factor_db: 0,
        lra: 0,
      },
      spectral: { centroid_hz: 0, rolloff_85_hz: 0, flatness: 0, tilt_db_per_octave: 0, octave_bands_db: [] },
      stereo: { correlation: 1, ms_ratio: 1, width_by_band: [] },
      transients: { density_per_sec: 0, onsets: [] },
      resonance_peaks_hz: [],
      sibilance_band_energy_db: -Infinity,
      computed_by: { loudness: 'client', spectral: 'client', stereo: 'client', transients: 'client', resonance_peaks_hz: 'client', sibilance_band_energy_db: 'client' },
    };
  }
  const ls = analyses.map((a) => a.loudness);
  const ss = analyses.map((a) => a.spectral);
  const sts = analyses.map((a) => a.stereo);
  return {
    computed_at: nowIso(),
    audio_blob_hash: hash,
    loudness: {
      lufs_integrated: combineLufs(ls.map((l) => l.lufs_integrated)),
      lufs_short_term: combineLufs(ls.map((l) => l.lufs_short_term)),
      lufs_momentary_max: combineLufs(ls.map((l) => l.lufs_momentary_max)),
      true_peak_dbtp: Math.max(...ls.map((l) => l.true_peak_dbtp)),
      crest_factor_db: avgNumber(ls.map((l) => l.crest_factor_db)),
      lra: avgNumber(ls.map((l) => l.lra)),
    },
    spectral: {
      centroid_hz: avgNumber(ss.map((s) => s.centroid_hz)),
      rolloff_85_hz: avgNumber(ss.map((s) => s.rolloff_85_hz)),
      flatness: avgNumber(ss.map((s) => s.flatness)),
      tilt_db_per_octave: avgNumber(ss.map((s) => s.tilt_db_per_octave)),
      octave_bands_db: avgArrays(ss.map((s) => s.octave_bands_db)),
    },
    stereo: {
      correlation: avgNumber(sts.map((s) => s.correlation)),
      ms_ratio: avgNumber(sts.map((s) => s.ms_ratio)),
      width_by_band: avgArrays(sts.map((s) => s.width_by_band)),
    },
    transients: {
      density_per_sec: avgNumber(analyses.map((a) => a.transients.density_per_sec)),
      onsets: [],
    },
    resonance_peaks_hz: [],
    sibilance_band_energy_db: combineLufs(analyses.map((a) => a.sibilance_band_energy_db)),
    computed_by: {
      loudness: 'client',
      spectral: 'client',
      stereo: 'client',
      transients: 'client',
      resonance_peaks_hz: 'client',
      sibilance_band_energy_db: 'client',
    },
  };
}

// --- analyzeTrack -----------------------------------------------------------

interface AnalyzeTrackParams {
  track_id: string;
  force_recompute?: boolean;
}

async function analyzeTrack(params: AnalyzeTrackParams): Promise<TrackAnalysis> {
  if (!params || typeof params.track_id !== 'string') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'analyzeTrack: track_id required');
  }
  const tracks = getSessionTracks();
  const track = tracks.find((t) => t.id === params.track_id);
  if (!track) {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, `Track ${params.track_id} does not exist`, {
      code: 'TRACK_NOT_FOUND',
      track_id: params.track_id,
    });
  }
  if (params.force_recompute) {
    const hash = track.regions?.[0]?.audio_blob_hash ?? '';
    if (hash) analysisCache.delete(hash);
  }
  const analysis = await analyzeTrackById(track);
  if (!analysis) {
    throw new ToolError(
      ERROR_CODES.INVALID_PARAMS,
      `Track ${params.track_id} has no audio loaded or audio blob not found in storage`,
      { code: 'TRACK_NOT_FOUND', track_id: params.track_id },
    );
  }
  return analysis;
}

// --- analyzeMix -------------------------------------------------------------

interface AnalyzeMixParams {
  force_recompute?: boolean;
}

interface AnalyzedTrack extends TrackAnalysis {
  track_id: string;
  track_name: string;
}

interface AnalyzeMixResult {
  tracks: AnalyzedTrack[];
  master: TrackAnalysis;
}

async function analyzeMix(params: AnalyzeMixParams): Promise<AnalyzeMixResult> {
  if (params?.force_recompute) analysisCache.clear();
  const tracks = getSessionTracks();
  const results = await Promise.all(tracks.map(analyzeTrackById));
  const analyzedTracks: AnalyzedTrack[] = [];
  const analysesForMaster: TrackAnalysis[] = [];
  for (let i = 0; i < results.length; i++) {
    const a = results[i];
    if (a) {
      const track = tracks[i];
      analyzedTracks.push({ ...a, track_id: track.id, track_name: track.name });
      analysesForMaster.push(a);
    }
  }
  return { tracks: analyzedTracks, master: combineMaster(analysesForMaster, '') };
}

// --- compareToReference -----------------------------------------------------

interface CompareToReferenceParams {
  current_track_id: string;
  reference_blob_hash: string;
}

interface DiffEntry {
  metric: string;
  current: number;
  reference: number;
  difference: number;
  unit: string;
  interpretation: string;
}

interface CompareToReferenceResult {
  diffs: DiffEntry[];
  summary_text: string;
}

async function compareToReference(
  params: CompareToReferenceParams,
): Promise<CompareToReferenceResult> {
  if (
    !params ||
    typeof params.current_track_id !== 'string' ||
    typeof params.reference_blob_hash !== 'string'
  ) {
    throw new ToolError(
      ERROR_CODES.INVALID_PARAMS,
      'compareToReference: current_track_id and reference_blob_hash required',
    );
  }

  const tracks = getSessionTracks();
  const track = tracks.find((t) => t.id === params.current_track_id);
  if (!track) {
    throw new ToolError(
      ERROR_CODES.INVALID_PARAMS,
      `Track ${params.current_track_id} does not exist`,
      { code: 'TRACK_NOT_FOUND', track_id: params.current_track_id },
    );
  }

  const [currentAnalysis, refBlob] = await Promise.all([
    analyzeTrackById(track),
    readAudioBlob(params.reference_blob_hash),
  ]);

  if (!currentAnalysis) {
    throw new ToolError(
      ERROR_CODES.INVALID_PARAMS,
      `Track ${params.current_track_id} has no audio loaded`,
      { code: 'TRACK_NOT_FOUND', track_id: params.current_track_id },
    );
  }
  if (!refBlob) {
    throw new ToolError(
      ERROR_CODES.INVALID_PARAMS,
      `Reference blob ${params.reference_blob_hash} not found in storage`,
      { code: 'REFERENCE_NOT_FOUND', reference_blob_hash: params.reference_blob_hash },
    );
  }

  const refAnalysis = await analyzeBlob(refBlob, params.reference_blob_hash);

  const diffs: DiffEntry[] = [
    makeDiff(
      'lufs_integrated',
      currentAnalysis.loudness.lufs_integrated,
      refAnalysis.loudness.lufs_integrated,
      'LUFS',
      'Integrated loudness difference',
    ),
    makeDiff(
      'true_peak_dbtp',
      currentAnalysis.loudness.true_peak_dbtp,
      refAnalysis.loudness.true_peak_dbtp,
      'dBTP',
      'True peak difference',
    ),
    makeDiff(
      'lra',
      currentAnalysis.loudness.lra,
      refAnalysis.loudness.lra,
      'LU',
      'Loudness range difference',
    ),
    makeDiff(
      'spectral_centroid_hz',
      currentAnalysis.spectral.centroid_hz,
      refAnalysis.spectral.centroid_hz,
      'Hz',
      'Spectral centroid frequency difference',
    ),
    makeDiff(
      'tilt_db_per_octave',
      currentAnalysis.spectral.tilt_db_per_octave,
      refAnalysis.spectral.tilt_db_per_octave,
      'dB/octave',
      'Spectral tilt difference (negative = brighter reference)',
    ),
    makeDiff(
      'stereo_correlation',
      currentAnalysis.stereo.correlation,
      refAnalysis.stereo.correlation,
      'ratio',
      'Stereo correlation difference',
    ),
  ];

  const lufsGap = currentAnalysis.loudness.lufs_integrated - refAnalysis.loudness.lufs_integrated;
  const summary =
    `Current track is ${Math.abs(lufsGap).toFixed(1)} LUFS ${lufsGap >= 0 ? 'louder' : 'quieter'} ` +
    `than the reference. ` +
    `Spectral centroid difference: ${(currentAnalysis.spectral.centroid_hz - refAnalysis.spectral.centroid_hz).toFixed(0)} Hz. ` +
    `Stereo correlation difference: ${(currentAnalysis.stereo.correlation - refAnalysis.stereo.correlation).toFixed(2)}.`;

  return { diffs, summary_text: summary };
}

function makeDiff(
  metric: string,
  current: number,
  reference: number,
  unit: string,
  interpretation: string,
): DiffEntry {
  return { metric, current, reference, difference: current - reference, unit, interpretation };
}

export const analysisHandlers = { analyzeTrack, analyzeMix, compareToReference } as const;

export function registerAnalysisHandlers(d: Dispatcher): void {
  d.register<AnalyzeTrackParams, TrackAnalysis>('analyzeTrack', analyzeTrack);
  d.register<AnalyzeMixParams, AnalyzeMixResult>('analyzeMix', analyzeMix);
  d.register<CompareToReferenceParams, CompareToReferenceResult>(
    'compareToReference',
    compareToReference,
  );
}
